/* ============================================================
   The corridor run — one consignment, every consequence.

   Every other tool here answers one question well and then stops. Cost
   per tonne-km does not know what the duty is. Landed cost does not know
   what the margin was. Emissions does not know either. So the operator
   gets four correct answers and has to hold the interaction between them
   in their head — which is exactly where the money is lost, because the
   interactions are what bite:

     · a demurrage tier that quietly exceeds the margin on the trip
     · a duty float that turns a profitable run into a financing problem
     · a modal choice that is cheaper per tonne-km and worse on carbon,
       in a tender that scores both

   This module composes the existing engines over ONE input set and then
   looks for those interactions. The composition is the easy part. The
   findings are the product.

   WHAT IT WILL NOT DO. It does not invent inputs. Every engine here
   already refuses to guess — a blank payload does not become 1 tonne, an
   unbenchmarked mode says so rather than producing a bare number — and
   composing them must not quietly reintroduce the defaults each of them
   was careful to reject. Anything not supplied is reported as not
   supplied, and any finding that depends on it is withheld rather than
   computed against a placeholder.
   ============================================================ */

import { assessDuty, assessDemurrage, marginalDayCost, dutyBand } from './duty.js';
import { assessChain } from './emissions.js';
import { compareToBenchmark, getBenchmark, getBand, bandGap } from './benchmarks.js';
import { cashCycle, workingCapitalGap } from './finance.js';
import { regulationsFor } from './regulations.js';

/** Bumped when the shape changes, so a stored run can be read honestly. */
export const RUN_SCHEMA_VERSION = 1;

/** Which GLEC mode a corridor mode maps onto, for the carbon leg. */
const EMISSIONS_MODE = { road: 'road-artic', rail: 'rail-diesel' };

/**
 * @param {object} input
 *   corridorKey   key into BENCHMARKS
 *   mode          'road' | 'rail'
 *   distance      km, one way
 *   payload       tonnes
 *   containers    boxes on this run
 *   tripCost      total variable cost of the run, USD
 *   tripRevenue   what the customer pays for it, USD
 *   cif           customs value, USD — optional
 *   dutyBandId    EAC CET band id
 *   freeDays      contractual free time
 *   daysHeld      days the box has been, or will be, held
 *   dso           debtor days — optional
 *   fuelLitres    measured fuel for the run — optional, upgrades carbon
 *   operatorType  'importer' | 'clearing-agent' | 'transporter' | 'warehouse'
 */
export function assessRun(input, now = new Date()) {
  const {
    corridorKey,
    mode = 'road',
    distance,
    payload,
    containers = 1,
    tripCost,
    tripRevenue,
    cif,
    dutyBandId = 'zero',
    freeDays = 0,
    daysHeld = 0,
    dso,
    fuelLitres,
    operatorType
  } = input;

  const missing = [];
  const has = (v) => Number.isFinite(v) && v > 0;

  /* ---- 1. Cost, against the published band ---- */
  const tonneKm = has(payload) && has(distance) ? payload * distance : null;
  const costPerTonKm = tonneKm && has(tripCost) ? tripCost / tonneKm : null;

  if (!has(distance)) missing.push('distance');
  if (!has(payload)) missing.push('payload');
  if (!has(tripCost)) missing.push('cost of the run');

  const comparison = costPerTonKm
    ? compareToBenchmark(corridorKey, mode, costPerTonKm)
    : { available: false, reason: 'Enter a distance, payload and trip cost.' };

  const bandForRun = corridorKey ? getBand(corridorKey, mode) : null;

  const cost = {
    tonneKm,
    costPerTonKm,
    comparison,
    gap: bandGap(costPerTonKm, bandForRun, tonneKm),
    band: bandForRun,
    benchmark: corridorKey ? getBenchmark(corridorKey) : null
  };

  /* ---- 2. Margin on the run ---- */
  const margin =
    has(tripRevenue) && has(tripCost)
      ? {
          revenue: tripRevenue,
          cost: tripCost,
          profit: tripRevenue - tripCost,
          marginPct: ((tripRevenue - tripCost) / tripRevenue) * 100
        }
      : null;
  if (!has(tripRevenue)) missing.push('revenue for the run');

  /* ---- 3. Duty and dwell ---- */
  const band = dutyBand(dutyBandId);
  const duty = has(cif) ? assessDuty({ cif, dutyRate: band.rate }) : null;
  const demurrage = assessDemurrage({ daysHeld, freeDays, containers });
  const nextDay = marginalDayCost({ daysHeld, freeDays, containers });

  /* ---- 4. Carbon ---- */
  const glecMode = EMISSIONS_MODE[mode] || 'road-artic';
  const carbon =
    has(distance) && has(payload)
      ? assessChain([
          { mode: glecMode, distance, payload, ...(has(fuelLitres) ? { fuelLitres } : {}) }
        ])
      : null;

  /* ---- 5. Cash ---- */
  // The duty float on THIS run: money out the day the box lands, back
  // when the customer settles. That is the dwell plus the debtor days,
  // not an abstract number the operator has to estimate separately.
  const dutyFloatDays = Number.isFinite(dso) ? daysHeld + dso : null;
  const ccc = dutyFloatDays !== null ? cashCycle({ dso, dpo: 0, dutyFloatDays }) : null;
  const cash =
    duty && dutyFloatDays !== null
      ? {
          dutyFloatDays,
          ccc,
          lockedUp: duty.totalTax
        }
      : null;

  /* ---- 6. What applies to you ---- */
  const obligations = operatorType
    ? regulationsFor(operatorType, now).filter((r) => r.status === 'in-force' || r.status === 'imminent')
    : [];

  const result = {
    schemaVersion: RUN_SCHEMA_VERSION,
    input,
    cost,
    margin,
    duty,
    demurrage,
    nextDay,
    carbon,
    cash,
    obligations,
    missing
  };

  result.findings = findings(result);
  result.breakingPoints = breakingPoints(result);
  return result;
}

/* ============================================================
   What breaks first
   ============================================================ */

/**
 * The point at which this run stops working, one lever at a time.
 *
 * "Your margin survives a 4% cost increase" is a different conversation
 * from "it survives 60%", and the run's own numbers already contain the
 * answer. What they do not contain is a reason to sweep a grid: every
 * relationship here except the demurrage tiers is linear, so the
 * crossing point is solved exactly. A swept table would invent a step
 * size and then report its own resolution as if it were precision.
 *
 * THREE RULES, the same ones the rest of this module works under.
 *
 * 1. Only levers the user actually supplied. No revenue entered means no
 *    margin to break, so no margin sensitivity — not a margin computed
 *    against an implicit zero.
 *
 * 2. Nothing beyond the horizon. A margin that survives doubling the
 *    cost is reported as "no plausible increase breaks it", not as
 *    +340%, because the linear model stops being about this business
 *    long before that.
 *
 * 3. Say which way the lever moves. "Costs rising 4%" and "revenue
 *    falling 4%" are not the same instruction to an operator even when
 *    the arithmetic matches.
 */
const COST_HORIZON = 1.0; // +100% — past here the model is not about this run
const DAY_HORIZON = 60;

/**
 * One scale, applied to the same question on every lever: how small is
 * the move that breaks this? A 20% rate cut and a 25% cost rise are both
 * worth USD 280 here, and the rate cut ranks higher because it is the
 * smaller move — not because revenue is inherently scarier than cost.
 * Duplicating thresholds per lever invited exactly that kind of unearned
 * judgement, so there is one function.
 */
export function fragility(fractionalMove) {
  if (fractionalMove < 0.1) return 'danger';
  if (fractionalMove < 0.25) return 'warning';
  return 'info';
}

function breakingPoints(r) {
  const { margin, demurrage, cost, input } = r;
  const out = [];

  // Margin against cost. Profit = revenue − cost − demurrage.
  if (margin && margin.profit > 0 && input.tripCost > 0) {
    const headroom = margin.profit / input.tripCost;
    out.push(
      headroom <= COST_HORIZON
        ? {
            lever: 'Cost of the run',
            direction: 'up',
            atLabel: `+${(headroom * 100).toFixed(headroom < 0.1 ? 1 : 0)}%`,
            detail:
              `Costs rising ${(headroom * 100).toFixed(headroom < 0.1 ? 1 : 0)}% — ` +
              `${fmt(margin.profit)} of headroom on ${fmt(input.tripCost)} — takes this run to zero.`,
            severity: fragility(headroom)
          }
        : {
            lever: 'Cost of the run',
            direction: 'up',
            atLabel: 'beyond +100%',
            detail: 'No plausible cost increase on this run takes the margin to zero.',
            severity: 'info'
          }
    );
  }

  // Margin against revenue, which is the same arithmetic and a different
  // instruction — one is a procurement problem, the other is a pricing one.
  if (margin && margin.profit > 0 && input.tripRevenue > 0) {
    const drop = margin.profit / input.tripRevenue;
    out.push({
      lever: 'Revenue for the run',
      direction: 'down',
      atLabel: `−${(drop * 100).toFixed(drop < 0.1 ? 1 : 0)}%`,
      detail:
        `A rate cut of ${(drop * 100).toFixed(drop < 0.1 ? 1 : 0)}% wipes out the margin. ` +
        `That is ${fmt(margin.profit)} on a ${fmt(input.tripRevenue)} rate.`,
      severity: fragility(drop)
    });
  }

  // Days at the terminal. Stepped rather than solved, because the tiers
  // are piecewise — and stepped through the real engine rather than a
  // reimplementation of it, so the two cannot disagree.
  if (margin && margin.profit > 0) {
    let day = input.daysHeld ?? 0;
    let broke = null;
    while (day < DAY_HORIZON) {
      day += 1;
      const at = assessDemurrage({
        daysHeld: day,
        freeDays: input.freeDays ?? 0,
        containers: input.containers ?? 1
      });
      if (at.total >= margin.profit) {
        broke = { day, total: at.total };
        break;
      }
    }
    const extra = broke ? broke.day - (input.daysHeld ?? 0) : null;
    out.push(
      broke
        ? {
            lever: 'Days at the terminal',
            direction: 'up',
            atLabel: `+${extra} day${extra === 1 ? '' : 's'}`,
            detail:
              `${extra} more day${extra === 1 ? '' : 's'} of dwell puts demurrage at ` +
              `${fmt(broke.total)}, past the ${fmt(margin.profit)} of profit on this run.`,
            // Days are absolute, not fractional: expressed against the
            // horizon so the same scale still answers the same question.
            severity: fragility(extra / 20)
          }
        : {
            lever: 'Days at the terminal',
            direction: 'up',
            atLabel: `beyond ${DAY_HORIZON} days`,
            detail: `Demurrage does not overtake the margin within ${DAY_HORIZON} days.`,
            severity: 'info'
          }
    );
  }

  // Payload against the published band: how empty the truck can run
  // before the cost per tonne-km leaves the band entirely.
  if (cost.comparison?.available && cost.costPerTonKm && input.payload > 0) {
    const minPayload = input.tripCost / (cost.comparison.high * input.distance);
    if (minPayload < input.payload) {
      const dropPct = ((input.payload - minPayload) / input.payload) * 100;
      out.push({
        lever: 'Payload',
        direction: 'down',
        atLabel: `−${dropPct.toFixed(0)}%`,
        detail:
          `Below ${minPayload.toFixed(1)} t the cost per tonne-km leaves the top of the ` +
          `published band. You are carrying ${input.payload} t.`,
        severity: fragility(dropPct / 100)
      });
    }
  }

  const rank = { danger: 0, warning: 1, info: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/* ============================================================
   The interactions — the part no single tool can see
   ============================================================ */

/**
 * Each finding states the arithmetic that produced it, because a
 * cross-domain claim the reader cannot check is worse than no claim.
 * Ordered by severity; the caller shows the top one first.
 */
function findings(r) {
  const out = [];
  const { margin, demurrage, nextDay, duty, cost, carbon, cash } = r;

  /* Demurrage against margin — the one that ends runs. */
  if (margin && margin.profit > 0 && demurrage.total > 0) {
    const share = (demurrage.total / margin.profit) * 100;
    if (demurrage.total >= margin.profit) {
      out.push({
        severity: 'danger',
        title: 'Demurrage has already exceeded the margin on this run',
        detail:
          `${fmt(demurrage.total)} accrued against ${fmt(margin.profit)} of profit. ` +
          `The run is now loss-making, and the next day adds ${fmt(nextDay)}.`
      });
    } else if (share >= 40) {
      out.push({
        severity: 'warning',
        title: 'Demurrage is eating the margin',
        detail:
          `${fmt(demurrage.total)} is ${Math.round(share)}% of the ${fmt(margin.profit)} profit. ` +
          `At ${fmt(nextDay)} a day, ${daysToBreakEven(margin.profit - demurrage.total, nextDay)}.`
      });
    }
  }

  /* Free time still in hand — the actionable version of the same fact. */
  if (demurrage.total === 0 && demurrage.daysRemaining > 0 && nextDay > 0) {
    out.push({
      severity: 'info',
      title: `${plural(demurrage.daysRemaining, 'day')} of free time left`,
      detail: `Nothing has accrued yet. The first chargeable day costs ${fmt(nextDay)}.`
    });
  }

  /* Duty against margin — the financing problem hiding inside a profitable run. */
  if (margin && duty && duty.totalTax > 0 && margin.profit > 0) {
    const multiple = duty.totalTax / margin.profit;
    if (multiple >= 3) {
      out.push({
        severity: 'warning',
        title: 'The duty dwarfs the margin on this run',
        detail:
          `${fmt(duty.totalTax)} payable to KRA on arrival against ${fmt(margin.profit)} of profit — ` +
          `${multiple.toFixed(1)}×. The run is profitable and still a financing problem: the cash ` +
          `goes out before the customer settles.`
      });
    }
  }

  /* Cash: how long the duty is out of the business. */
  if (cash && cash.dutyFloatDays > 0 && duty && duty.totalTax > 0) {
    out.push({
      severity: cash.dutyFloatDays > 60 ? 'warning' : 'info',
      title: `${fmt(duty.totalTax)} is out of the business for ${plural(cash.dutyFloatDays, 'day')}`,
      detail:
        `Duty paid on arrival, settled ${plural(r.input.dso, 'day')} after invoice, with ` +
        `${plural(r.input.daysHeld, 'day')} of dwell before that. Multiply by the runs you have ` +
        `in flight at once to size the facility you actually need.`
    });
  }

  /* Cost against the published band. compareToBenchmark returns a
     status keyed to low/median/high; only the top of that range is
     worth interrupting the reader for. */
  if (cost.comparison?.available && cost.costPerTonKm) {
    const c = cost.comparison;
    if (c.status === 'poor' || c.status === 'fair') {
      const overHigh = ((cost.costPerTonKm - c.high) / c.high) * 100;
      out.push({
        severity: c.status === 'poor' ? 'warning' : 'info',
        title:
          c.status === 'poor'
            ? 'Above the top of the published band for this corridor'
            : 'In the upper half of the published band',
        detail:
          `$${cost.costPerTonKm.toFixed(3)} per tonne-km against a band of ` +
          `$${c.low.toFixed(3)}–$${c.high.toFixed(3)} (median $${c.median.toFixed(3)})` +
          (c.status === 'poor' ? `, ${Math.round(overHigh)}% over the top` : '') +
          `. Band as at ${cost.benchmark?.asOf || 'the stated date'}, ${c.band.provenance}.` +
          (cost.gap
            ? ` Reaching the median is worth ${fmt(cost.gap.toMedian)} on this run; the low end ` +
              `of the band, ${fmt(cost.gap.toLow)}.`
            : '')
      });
    }
  }

  /* Carbon: say plainly when the number is default-derived, because a
     shipper will discount it and the operator should know before the
     tender, not after. */
  if (carbon && carbon.quality === 'default') {
    out.push({
      severity: 'info',
      title: 'The carbon figure is GLEC-default, not measured',
      detail:
        `${carbon.intensity.toFixed(1)} gCO₂e per tonne-km, derived from the framework default ` +
        `for this mode. Reportable, and a shipper will discount it against a competitor's ` +
        `measured figure. Entering the fuel burnt on this run upgrades it to primary data.`
    });
  }

  const rank = { danger: 0, warning: 1, info: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/* ---- small helpers, local so the module stays self-contained ---- */

function fmt(value) {
  if (!Number.isFinite(value)) return '—';
  return `USD ${Math.round(value).toLocaleString('en-GB')}`;
}

function plural(count, unit) {
  const n = Math.round(count);
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function daysToBreakEven(headroom, perDay) {
  if (!Number.isFinite(headroom) || !Number.isFinite(perDay) || perDay <= 0) return 'the margin is gone';
  const days = Math.floor(headroom / perDay);
  if (days <= 0) return 'one more day wipes out what is left';
  return `${plural(days, 'more day')} before the run breaks even`;
}
